import React, { ReactElement, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch } from 'src/store/hooks'
import {
  createPendingCEBrokerFlorida,
  createPendingCEBrokerFloridaCourse,
  fetchEventAccreditations,
  fetchCourseAccreditations,
} from 'src/v3/content-pipeline/accreditation.slice'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { AccreditationStatus } from '../../shared-types'
import { StatusDropdownButton } from '../shared'
import {
  CourseCodeField,
  ProfessionCodeField,
  SubjectAreaField,
  NotesField,
  DateField,
  FieldsRow,
} from './form-fields'

type CreateFormProps = {
  eventId?: string
  courseId?: string
  open: boolean
  setOpen: (isOpen: boolean) => void
  onSuccess: () => void
}

export let CreateForm = ({
  eventId,
  courseId,
  open,
  setOpen,
  onSuccess,
}: CreateFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [selectedStatus, setSelectedStatus] =
    useState<AccreditationStatus>('Draft')

  let form = useForm({
    defaultValues: {
      courseCode: '',
      professionCode: '',
      subjectArea: '',
      expirationDate: '',
      notes: '',
    },
    onSubmit: ({ value }) => {
      let payload = {
        status: selectedStatus,
        courseCode: value.courseCode || null,
        professionCode: value.professionCode,
        subjectArea: value.subjectArea,
        state: 'Florida' as const,
        expirationDate: value.expirationDate || null,
        notes: value.notes || null,
      }
      if (courseId) {
        dispatch(createPendingCEBrokerFloridaCourse({ courseId, ...payload }))
        dispatch(fetchCourseAccreditations(courseId))
      } else if (eventId) {
        dispatch(createPendingCEBrokerFlorida({ eventId, ...payload }))
        dispatch(fetchEventAccreditations(eventId))
      }
      form.reset()
      setSelectedStatus('Draft')
      setOpen(false)
      onSuccess()
    },
  })

  return (
    <Dialog
      open={open}
      onClose={() => {
        form.reset()
        setOpen(false)
      }}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create Pending CEBroker Florida Accreditation</DialogTitle>
      <DialogContent>
        <Box className="grid gap-6 mt-2">
          <Typography color="text.secondary" variant="body2">
            Create a pending CEBroker Florida accreditation for review. This
            will be submitted for approval before being finalized.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => <CourseCodeField field={field} />}
          />

          <FieldsRow>
            <form.Field
              name="professionCode"
              children={(field) => <ProfessionCodeField field={field} />}
            />
            <form.Field
              name="subjectArea"
              children={(field) => <SubjectAreaField field={field} />}
            />
          </FieldsRow>

          <form.Field
            name="expirationDate"
            children={(field) => (
              <DateField field={field} label="Expiration Date" />
            )}
          />

          <form.Field
            name="notes"
            children={(field) => <NotesField field={field} />}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            form.reset()
            setSelectedStatus('Draft')
            setOpen(false)
          }}
        >
          Cancel
        </Button>
        <StatusDropdownButton
          selectedStatus={selectedStatus}
          onStatusChange={setSelectedStatus}
          onSubmit={() => form.handleSubmit()}
        />
      </DialogActions>
    </Dialog>
  )
}
